/**
 * Inventory domain types.
 *
 * Talbert's inventory spans four physical zones and four distinct kinds
 * of stock that behave differently:
 *
 *   VENEERS       — tracked by bundle (a bundle is ~24 consecutive leaves
 *                   flitched from one log). Key attributes: species, cut,
 *                   figure, match, flitch #, sq ft, FSC chain of custody.
 *   SUBSTRATES    — sheet goods (MDF, particleboard, plywood) by sheet.
 *   DOORS         — finished or WIP doors by unit.
 *   PANELS        — finished or WIP panels by unit.
 *   HARDWARE      — hinges, edgebanding, adhesive, fasteners — small parts.
 *   FINISH        — Perfecta UV finish materials by gallon / roll.
 *
 * Rather than forcing everything into one rigid schema, we use a flexible
 * `InventoryItem` with typed `kind` and optional kind-specific fields.
 *
 * Stock movements are append-only — every receipt, adjustment, transfer,
 * and consumption creates a Movement record. Current on-hand is derived.
 */

export type InventoryKind =
  | 'veneer'
  | 'substrate'
  | 'door'
  | 'panel'
  | 'hardware'
  | 'finish'
  | 'other';

export const INVENTORY_KIND_LABELS: Record<InventoryKind, string> = {
  veneer:    'Veneer',
  substrate: 'Substrate',
  door:      'Door',
  panel:     'Panel',
  hardware:  'Hardware',
  finish:    'Finish',
  other:     'Other',
};

export type Location =
  | 'veneer_vault'
  | 'panel_line'
  | 'door_line'
  | 'finish_line'
  | 'finished_goods'
  | 'receiving'
  | 'shipping';

export const LOCATION_LABELS: Record<Location, string> = {
  veneer_vault:   'Veneer Vault',
  panel_line:     'Panel Line',
  door_line:      'Door Line',
  finish_line:    'Finish Line',
  finished_goods: 'Finished Goods',
  receiving:      'Receiving',
  shipping:       'Shipping',
};

export type UnitOfMeasure =
  | 'ea'      // each
  | 'sf'      // square foot (veneer)
  | 'sheet'   // 4x8, 4x10 sheets
  | 'lf'      // linear foot
  | 'gal'     // gallons (finish)
  | 'roll'    // edgebanding
  | 'bundle'; // veneer bundle

// ─────────── Veneer-specific ───────────

export type VeneerCut =
  | 'plain_sliced'
  | 'quarter_sliced'
  | 'rift_cut'
  | 'rotary_cut'
  | 'half_round'
  | 'rift_quartered';

export const VENEER_CUT_LABELS: Record<VeneerCut, string> = {
  plain_sliced:    'Plain Sliced',
  quarter_sliced:  'Quarter Sliced',
  rift_cut:        'Rift Cut',
  rotary_cut:      'Rotary Cut',
  half_round:      'Half Round',
  rift_quartered:  'Rift · Quartered',
};

export type VeneerFigure =
  | 'none'
  | 'quilted'
  | 'flame'
  | 'birdseye'
  | 'fiddleback'
  | 'burl'
  | 'crotch'
  | 'pommele';

export const VENEER_FIGURE_LABELS: Record<VeneerFigure, string> = {
  none:        'Plain',
  quilted:     'Quilted',
  flame:       'Flame',
  birdseye:    "Bird's Eye",
  fiddleback:  'Fiddleback',
  burl:        'Burl',
  crotch:      'Crotch',
  pommele:     'Pommele',
};

export type VeneerMatch = 'book' | 'slip' | 'random' | 'balance' | 'end_match' | 'herringbone';

export const VENEER_MATCH_LABELS: Record<VeneerMatch, string> = {
  book:        'Book Match',
  slip:        'Slip Match',
  random:      'Random Match',
  balance:     'Balance Match',
  end_match:   'End Match',
  herringbone: 'Herringbone',
};

// ─────────── Inventory item ───────────

export type InventoryItem = {
  id: string;
  sku: string;               // 'VEN-WAL-QTR-0412'
  name: string;              // 'Walnut, Quartered, Figured — Flitch 0412'
  kind: InventoryKind;
  description?: string;
  tags?: string[];

  // Units & pricing
  unit: UnitOfMeasure;
  unitCost?: number;         // what we paid (for valuation)
  unitPrice?: number;        // what we sell at (for quick quote pricing)

  // Stock control
  reorderPoint?: number;      // trigger low-stock alert
  reorderQty?: number;        // suggested PO quantity
  preferredSupplier?: string;

  // Veneer-specific
  veneerSpecies?: string;     // "Walnut", "White Oak", "Figured Maple"
  veneerCut?: VeneerCut;
  veneerFigure?: VeneerFigure;
  veneerMatch?: VeneerMatch;
  flitchNumber?: string;      // '0412'
  sqFtPerBundle?: number;

  // FSC chain-of-custody
  fscCertified?: boolean;
  fscClaim?: string;          // 'FSC 100%', 'FSC Mix Credit', etc.

  // Dimensions (for panels/doors/substrate)
  dimensions?: string;        // '4' x 8' x 3/4""
  thickness?: string;
  grade?: string;             // 'A1', 'AA', etc.

  active: boolean;            // soft-delete flag
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;

  // Derived — filled in by the backend for list/detail views
  onHand?: Record<Location, number>;     // qty per location
  totalOnHand?: number;                  // sum across locations
  valueAtCost?: number;                  // totalOnHand × unitCost
};

// ─────────── Stock movements ───────────

export type MovementType =
  | 'receipt'       // new stock arriving
  | 'adjustment'    // cycle count correction
  | 'transfer'      // moving between locations
  | 'consumption'   // used on a work order
  | 'return'        // returned to stock
  | 'scrap'         // wasted / damaged
  | 'initial';      // initial count when item was added

export const MOVEMENT_TYPE_LABELS: Record<MovementType, string> = {
  receipt:     'Receipt',
  adjustment:  'Adjustment',
  transfer:    'Transfer',
  consumption: 'Consumption',
  return:      'Return',
  scrap:       'Scrap',
  initial:     'Initial Count',
};

export type StockMovement = {
  id: string;
  itemId: string;
  itemSku?: string;    // denormalized for log views
  itemName?: string;
  type: MovementType;

  /** Positive for stock-in; negative for stock-out. `transfer` has
   * two records: one negative at `from`, one positive at `to`. */
  quantity: number;
  location: Location;
  toLocation?: Location;        // only for transfer

  // Links back to source document
  orderId?: string;
  orderNumber?: string;
  poNumber?: string;
  reason?: string;

  // For veneers — which flitch/batch was touched
  batch?: string;
  lotNumber?: string;

  unitCostAtTime?: number;      // for FIFO / weighted average valuation

  occurredAt: string;
  authorUid: string;
  authorName?: string;
  notes?: string;
  createdAt?: string;
};

// ─────────── Helpers ───────────

export function isLowStock(item: InventoryItem): boolean {
  if (item.reorderPoint === undefined) return false;
  if (item.totalOnHand === undefined) return false;
  return item.totalOnHand <= item.reorderPoint;
}

export function isOutOfStock(item: InventoryItem): boolean {
  return (item.totalOnHand ?? 0) <= 0;
}
